import { ThemeProvider } from "@emotion/react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { deDE as coreDeDE } from "@mui/material/locale";
import { createTheme } from "@mui/material/styles";
import {
  DataGrid,
  GridColDef,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid";
import { deDE } from "@mui/x-data-grid/locales";
// import { deDE as pickersDeDE } from "@mui/x-date-pickers/locales";
import CloseIcon from "@mui/icons-material/Close";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import { PlayerStatistic } from "../interfaces";
import PlayerStatisticsDownloadButton from "./PlayerStatisticsDownloadButton";
import { useSeason } from "./SeasonContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface Player {
  id: number;
  name: string;
  team: number;
  nickname: string;
}

export interface Season {
  id: number;
  name: string;
}

export interface TeamStatistic {
  team_id: number;
  team_name: string;
  players: PlayerStatistic[];
}

interface PlayerStatisticsTabsProps {
  data: TeamStatistic[];
}

const PlayerStatisticsGrid: React.FC<PlayerStatisticsTabsProps> = ({
  data,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [singleSortModel, setSingleSortModel] = useState<GridSortModel>([
    { field: "single_matches_played", sort: "desc" as GridSortDirection },
  ]);
  const [doubleSortModel, setDoubleSortModel] = useState<GridSortModel>([
    { field: "double_matches_played", sort: "desc" as GridSortDirection },
  ]);
  const { seasonName } = useSeason();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlayerProgression, setSelectedPlayerProgression] = useState<
    { date: string; darts: number }[]
  >([]);
  const theme = createTheme(
    {
      palette: {
        primary: { main: "#1976d2" },
      },
    },
    deDE, // x-data-grid translations
    coreDeDE // core translations
  );

  // Create aggregated data for all players from all teams
  const allPlayersData: TeamStatistic = {
    team_id: 0,
    team_name: "Alle Teams",
    players: data
      .flatMap((team) => team.players)
      .filter((player) => player.single_matches_played + player.double_matches_played >= 1)
      .sort((a, b) => {
        const aWonPct = parseFloat(String(a.single_matches_won_pct || "0"));
        const bWonPct = parseFloat(String(b.single_matches_won_pct || "0"));
        return bWonPct - aWonPct;
      }),
  };

  const [selectedTeam, setSelectedTeam] = useState(0);
  const [teamData, setTeamData] = useState<TeamStatistic>(allPlayersData);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    // newValue is now the team_id directly
    console.log(newValue);
    setSelectedTeam(newValue);

    if (newValue === 0) {
      // "alle" tab selected
      setTeamData(allPlayersData);
    } else {
      const filtered = data.find((team_data) => team_data.team_id === newValue);

      console.log(newValue, data, filtered);
      if (!filtered) return;

      setTeamData(filtered);
    }
  };

  const handleRowClick = (stat: PlayerStatistic) => {
    setSelectedPlayerProgression(stat.singles_progression);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedPlayerProgression([]);
  };

  const handleSingleSortModelChange = (sortModel: any) => {
    setSingleSortModel(sortModel);
  };
  const handleDoubleSortModelChange = (sortModel: any) => {
    setDoubleSortModel(sortModel);
  };

  const chartData = {
    labels: selectedPlayerProgression.map((p) => p.date),
    datasets: [
      {
        label: "Darts",
        data: selectedPlayerProgression.map((p) => p.darts),
        borderColor: "rgba(75,192,192,1)",
        backgroundColor: "rgba(75,192,192,0.2)",
        tension: 0.3,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: "Saison-Verlauf" },
    },
    scales: {
      x: { title: { display: true, text: "Date" } },
      y: {
        title: { display: true, text: "Darts" },
        beginAtZero: false,
        reverse: true,
      },
    },
  };

  const showOnlyPlayerFirstName = (name: String) => {
    return name.split(" ")[0];
  };

  const baseProperties = {
    sortable: !isMobile,
    disableColumnMenu: true,
    hideSortIcons: isMobile,
  };

  const singleColumns: GridColDef<PlayerStatistic>[] = [
    {
      field: "player",
      headerName: "Name",
      valueGetter: (player: Player) => {
        if (!player) return "";
        return showOnlyPlayerFirstName(player.name) ?? "";
      },
      width: 120,
      ...baseProperties,
    },
    {
      field: "single_matches_played",
      headerName: "Gespielte Einzel",
      ...baseProperties,
    },
    {
      field: "single_matches_won_pct",
      headerName: "…davon gewonnen",

      width: 80,
      valueGetter: (pct: string) => `${pct}%`,
      sortComparator: (v1, v2) => {
        const num1 = parseFloat(v1.replace("%", ""));
        const num2 = parseFloat(v2.replace("%", ""));
        return num1 - num2;
      },

      ...baseProperties,
    },
    {
      field: "single_legs_played",
      headerName: "Gespielte Legs",
      width: 75,
      ...baseProperties,
    },
    {
      field: "single_legs_won_pct",
      headerName: "…davon gewonnen",

      width: 80,
      valueGetter: (pct: string) => `${pct}%`,
      sortComparator: (v1, v2) => {
        const num1 = parseFloat(v1.replace("%", ""));
        const num2 = parseFloat(v2.replace("%", ""));
        return num1 - num2;
      },

      ...baseProperties,
    },
    {
      field: "single_three_dart_average",
      headerName: "Ø 3-Dart",
      ...baseProperties,
    },

    {
      field: "single_three_dart_average_opp",
      headerName: "3-Dart Ø Gegner",
      ...baseProperties,
    },
    {
      field: "single_best_leg",
      headerName: "Bestes Leg",
      description: "Das beste gewonnene Leg",
      width: 60,
      ...baseProperties,
    },
    {
      field: "single_worst_leg",
      headerName: "Schlechtestes Leg",
      description: "Das schlechteste gewonnene Leg",
      ...baseProperties,
    },
    {
      field: "single_leg_std_dev",
      headerName: "Abw. Ø",
      description: "Ø der Abweichungen vom Leg-Ø",

      width: 25,
      ...baseProperties,
    },
    { field: "highscore_count", headerName: "171er bis 180er", width: 70 },
    {
      field: "highfinishes",
      headerName: "Highfinishes",
      description: "Highfinishes aus Einzel und Doppel",
      ...baseProperties,
    },
  ];

  const doubleColumns: GridColDef<PlayerStatistic>[] = [
    {
      field: "player",
      headerName: "Name",
      valueGetter: (player: Player) => {
        if (!player) return "";
        return showOnlyPlayerFirstName(player.name) ?? "";
      },
      width: 120,
      ...baseProperties,
    },
    {
      field: "double_matches_played",
      headerName: "Gespielte Doppel",

      ...baseProperties,
    },
    {
      field: "double_matches_won_pct",
      headerName: "...davon gewonnen",

      width: 80,

      valueGetter: (pct: string) => `${pct}%`,
      sortComparator: (v1, v2) => {
        const num1 = parseFloat(v1.replace("%", ""));
        const num2 = parseFloat(v2.replace("%", ""));
        return num1 - num2;
      },
      ...baseProperties,
    },

    {
      field: "double_legs_played",
      headerName: "Gespielte Legs",
      width: 75,

      ...baseProperties,
    },
    {
      field: "double_legs_won_pct",
      headerName: "...davon gewonnen",
      width: 80,

      valueGetter: (pct: string) => `${pct}%`,
      sortComparator: (v1, v2) => {
        const num1 = parseFloat(v1.replace("%", ""));
        const num2 = parseFloat(v2.replace("%", ""));
        return num1 - num2;
      },
      ...baseProperties,
    },
    {
      field: "double_three_dart_average",
      headerName: "Ø 3-Dart",

      ...baseProperties,
    },
    {
      field: "double_three_dart_average_opp",
      headerName: "3-Dart Ø Gegner",

      ...baseProperties,
    },
    {
      field: "double_best_leg",
      headerName: "Bestes Leg",
      description: "Das beste gewonnene Leg",

      ...baseProperties,
    },
    {
      field: "double_worst_leg",
      headerName: "Schlechtestes Leg",
      description: "Das schlechteste gewonnene Leg",
      ...baseProperties,
    },
    {
      field: "double_leg_std_dev",
      headerName: "Abw. Ø",
      description: "Ø der Abweichungen vom Leg-Ø",
      ...baseProperties,
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {teamData && (
        <PlayerStatisticsDownloadButton
          rows={teamData.players}
          singleColumns={singleColumns}
          doubleColumns={doubleColumns}
          team_name={teamData.team_name}
          season={seasonName}
          singleSortModel={singleSortModel}
          doubleSortModel={doubleSortModel}
        />
      )}
      <Tabs
        value={selectedTeam}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          "& .MuiTabs-scrollButtons": {
            "&.Mui-disabled": {
              opacity: 0.3,
            },
          },
        }}
      >
        <Tab
          label="alle"
          value={0}
          sx={{
            fontSize: "0.75rem",
            lineHeight: 1.2,
            minHeight: "auto",
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "auto",
            px: 2,
            fontWeight: "bold",
            backgroundColor: "#e0e0e0",
          }}
        />
        {data.map((team) => (
          <Tab
            key={team.team_id}
            label={team.team_name}
            value={team.team_id}
            sx={{
              fontSize: "0.75rem",
              lineHeight: 1.2,
              minHeight: "auto",
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: "auto",
              px: 2,
            }}
          />
        ))}
      </Tabs>

      {selectedTeam === 0 && (
        <>
          <Box
            sx={{
              mt: 1,
              pt: 3,
              overflowX: "auto",
              bgcolor: "#fcfcfc",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: "center", fontSize: "1rem" }}
            >
              Spielerstatistik - Einzel - {allPlayersData.team_name}
            </Typography>
            <div style={{ width: "100%" }}>
              <ThemeProvider theme={theme}>
                <DataGrid
                  showToolbar
                  rows={allPlayersData.players}
                  onRowClick={(params) => handleRowClick(params.row)}
                  columns={singleColumns}
                  getRowId={(row) => row.id}
                  onSortModelChange={handleSingleSortModelChange}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                    sorting: {
                      sortModel: [
                        {
                          field: "single_matches_played",
                          sort: "desc" as GridSortDirection,
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[100]}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                      lineHeight: "normal",
                    },
                  }}
                />
              </ThemeProvider>
            </div>
          </Box>
          <Box
            sx={{
              mt: 1,
              pt: 3,
              overflowX: "auto",
              bgcolor: "#fcfcfc",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: "center", fontSize: "1rem" }}
            >
              Spielerstatistik - Doppel - {allPlayersData.team_name}
            </Typography>
            <div style={{ width: "100%", minWidth: 900 }}>
              <ThemeProvider theme={theme}>
                <DataGrid
                  rows={allPlayersData.players}
                  columns={doubleColumns}
                  onSortModelChange={handleDoubleSortModelChange}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                    sorting: {
                      sortModel: [
                        {
                          field: "double_matches_played",
                          sort: "desc" as GridSortDirection,
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[100]}
                  disableRowSelectionOnClick
                  density="compact"
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                      lineHeight: "normal",
                    },
                  }}
                />
              </ThemeProvider>
            </div>
          </Box>
        </>
      )}

      {data.map((team) => (
        <div key={team.team_id}>
          {/* Team id: {team.team_id} */}
          {selectedTeam === team.team_id && (
            <>
              <Box
                sx={{
                  // border: "2px solid #000",
                  mt: 1,
                  pt: 3,
                  overflowX: "auto",
                  bgcolor: "#fcfcfc",
                  // borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ mb: 2, textAlign: "center", fontSize: "1rem" }}
                >
                  Spielerstatistik - Einzel - {team.team_name}
                </Typography>
                <div style={{ width: "100%" }}>
                  <ThemeProvider theme={theme}>
                    <DataGrid
                      showToolbar
                      rows={team.players}
                      onRowClick={(params) => handleRowClick(params.row)}
                      columns={singleColumns}
                      getRowId={(row) => row.id}
                      onSortModelChange={handleSingleSortModelChange}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 15 } },
                        sorting: {
                          sortModel: singleSortModel,
                        },
                      }}
                      density="compact"
                      // pageSizeOptions={[5, 10]}
                      disableRowSelectionOnClick
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          fontSize: "0.8rem",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          whiteSpace: "normal",
                          lineHeight: "normal",
                        },
                      }}
                    />
                  </ThemeProvider>
                </div>
              </Box>
              <Box
                sx={{
                  // border: "2px solid #000",
                  mt: 1,
                  pt: 3,
                  overflowX: "auto",
                  bgcolor: "#fcfcfc",
                  // borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ mb: 2, textAlign: "center", fontSize: "1rem" }}
                >
                  Spielerstatistik - Doppel - {team.team_name}
                </Typography>
                <div style={{ width: "100%", minWidth: 900 }}>
                  <ThemeProvider theme={theme}>
                    <DataGrid
                      rows={team.players}
                      columns={doubleColumns}
                      onSortModelChange={handleDoubleSortModelChange}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 15 } },
                        sorting: {
                          sortModel: doubleSortModel,
                        },
                      }}
                      // pageSizeOptions={[5, 10]}
                      disableRowSelectionOnClick
                      density="compact"
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          fontSize: "0.8rem",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          whiteSpace: "normal",
                          lineHeight: "normal",
                        },
                      }}
                    />
                  </ThemeProvider>
                </div>
              </Box>
            </>
          )}
        </div>
      ))}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Darts Verlauf (Einzel)
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Line data={chartData} options={chartOptions} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PlayerStatisticsGrid;
